import { ComponentType, useContext } from "react";
import { useLocation } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import SeparatorLineRaw from "app/components/ui/Separator/SeparatorLine";
import PenFilledIcon from "app/icons/navigation/PenFilledIcon";
import { ICON_SIZES } from "app/constants";
import Tag, { TAG_SIZES } from "app/components/ui/Tag/Tag";
import "./SearchButtonSkeleton.scss";

// SeparatorLine is an untyped JS component; re-type only the props we use here.
const SeparatorLine = SeparatorLineRaw as unknown as ComponentType<{ vertical?: boolean }>;

const SKELETON_HIGHLIGHT_COLOR = "#cdcdcd";

const Bar = ({ width }: { width: number }) => (
	<Skeleton
		highlightColor={SKELETON_HIGHLIGHT_COLOR}
		width={width}
		height={8}
		borderRadius={999}
	/>
);

const Separator = () => <span className="search-button-skeleton__separator">-</span>;

const RouteSDPSkeleton = () => (
	<div className="search-button-skeleton__route">
		<Bar width={64} />
		<Separator />
		<Bar width={64} />
	</div>
);

const RouteHotelOnlySkeleton = () => (
	<div className="search-button-skeleton__route">
		<Bar width={64} />
	</div>
);

const RouteStopOverSkeleton = () => (
	<div className="search-button-skeleton__route">
		<Bar width={64} />
		<Separator />
		<Tag size={TAG_SIZES.LARGE} className="search-button-skeleton__stopover">
			<Bar width={48} />
		</Tag>
		<Separator />
		<Bar width={64} />
	</div>
);

const DetailsSkeleton = ({ isMobile = false }: { isMobile?: boolean }) => (
	<div className="search-button-skeleton__details">
		<Bar width={74} />

		{isMobile ? (
			<span className="search-button-skeleton__dot">・</span>
		) : (
			<SeparatorLine vertical />
		)}

		<Bar width={158} />
	</div>
);

const SearchButtonSkeleton = () => {
	const { resolution } = useContext(AppGlobalsContext);
	const { pathname } = useLocation();
	const isStopover = pathname.includes("stopover");
	const isHotelOnly = pathname.includes("hotelonly");
	const isSDP = pathname.includes("sdp");
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	return (
		<div
			className="search-button-skeleton"
			data-testid="search-button-skeleton"
			aria-hidden="true"
		>
			{isMobile ? (
				<div className="search-button-skeleton__itinerary">
					{isHotelOnly && <RouteHotelOnlySkeleton />}
					{isStopover && <RouteStopOverSkeleton />}
					{isSDP && <RouteSDPSkeleton />}

					<DetailsSkeleton isMobile />
				</div>
			) : (
				<>
					{isHotelOnly && <RouteHotelOnlySkeleton />}
					{isStopover && <RouteStopOverSkeleton />}
					{isSDP && <RouteSDPSkeleton />}

					<SeparatorLine vertical />

					<DetailsSkeleton isMobile={false} />
				</>
			)}

			<PenFilledIcon
				className="search-button-skeleton__edit-icon"
				size={ICON_SIZES.LG}
				focusable="false"
				aria-hidden="true"
			/>
		</div>
	);
};

export default SearchButtonSkeleton;
